import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section } from '../ui/Section';
import { Button } from '../ui/Button';
import { Shield, Check, Star, Gift, ChevronDown, ChevronUp } from 'lucide-react';
import { trackAddToCart } from '../../hooks/useMetaPixel';
const includedItems = [{
  label: 'Cortisol Off — Manual Operativo',
  isBonus: false
}, {
  label: 'The 3PM Reset Checklist',
  image: 'https://cdn.magicpatterns.com/uploads/mZqGwT3m4xzkPu7dLVM9MS/Bono_1.png',
  isBonus: true,
  description: '¿Conoces ese momento exacto en que son las 3 de la tarde y tu cerebro simplemente se apaga? No puedes concentrarte. Saltas de tarea en tarea. Sientes una presión sorda en el pecho y sabes que las próximas 3 horas de trabajo van a ser un desperdicio. Esta checklist fue diseñada para ese momento exacto. 9 pasos. 3 minutos. Una sola página. No necesitas levantarte del escritorio. No necesitas cerrar tu laptop. No necesitas motivación. Ejecutas el paso 1, pasas al 2, y así hasta el 9. Cuando terminas, tu sistema nervioso ya cambió de estado. La neblina se fue. Vuelves al trabajo.',
  bullets: ['Sin teoría', 'Sin explicaciones largas', 'Diseñada para ejecutarse con una mano mientras tienes la otra en el teclado', 'Funciona en la primera ejecución'],
  quote: 'No entiendo cómo algo tan simple funciona tan rápido.'
}, {
  label: 'CEO Sleep Protocol — Guía de 5 Minutos',
  image: 'https://cdn.magicpatterns.com/uploads/sp4enRWoswGCRwJfUjnBWM/Bono_2.png',
  isBonus: true,
  description: 'El problema no es que no puedas dormir. El problema es que tu cerebro ejecutivo no sabe cómo apagarse. Llevas 12 horas tomando decisiones, resolviendo problemas y anticipando riesgos. Y cuando finalmente te acuestas, tu mente sigue corriendo: repasando conversaciones, calculando números, anticipando el día de mañana. Eso no es insomnio. Es un sistema nervioso que nadie le enseñó a desconectarse. Esta guía de una sola página tiene 6 acciones secuenciales para apagar tu cerebro ejecutivo al final del día.',
  bullets: ['La ejecutas en la cama', 'Sin pantallas', 'Sin apps', 'Sin ruido blanco', 'Sin pastillas', 'Funciona aunque hayas tenido el día más caótico de tu vida'],
  closing: 'Cuando terminas la secuencia, tu cuerpo ya recibió la señal biológica de que el día terminó. El sueño llega solo. Y mañana despiertas con claridad total. No aturdido. No arrastrándote. Listo para operar.'
}, {
  label: 'High-Performance Morning Checklist',
  image: 'https://cdn.magicpatterns.com/uploads/axRbfSC2nZStDYA4jm9WZq/Bono_3.png',
  isBonus: true,
  description: 'La mayoría de emprendedores empieza el día en modo reactivo. Abren el teléfono, revisan mensajes, leen emails y en 4 minutos ya están respondiendo a la agenda de otros. Ya perdieron el control del día antes de desayunar. Esta checklist existe para que eso no vuelva a pasarte. 7 pasos. 7 minutos. Un solo objetivo: arrancar en modo enfoque. Cada paso activa un estado fisiológico diferente. No es motivación. No es mentalidad positiva. Es biología aplicada a tu rendimiento. Cuando terminas el paso 7, ya estás operando. Tu corteza prefrontal está activa. Tu cortisol matutino está regulado. Tu enfoque está encendido.',
  bullets: ['Sin ritual espiritual', 'Sin rutina de 2 horas', 'Sin motivación requerida', 'Lista para usar mañana mismo', 'Compatible con cualquier horario, incluso los más caóticos'],
  closing: 'Esto es lo que hace la diferencia entre un CEO que reacciona todo el día y uno que ejecuta con intención desde la primera hora.'
}];
export function PricingSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  return <Section background="cream-dark" id="pricing" data-id="element-159">
      <div className="max-w-3xl mx-auto" data-id="element-160">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-2xl shadow-xl overflow-hidden border border-brand-gold/20" data-id="element-161">
          <div className="bg-brand-charcoal-dark p-8 text-center text-white relative overflow-hidden" data-id="element-162">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-163"></div>
            <h2 className="text-3xl font-serif font-bold mb-2 relative z-10" data-id="element-164">
              Oferta Especial Limitada
            </h2>
            <p className="text-brand-gold relative z-10" data-id="element-165">
              Invierte en tu salud mental y productividad
            </p>
          </div>

          <div className="p-8 md:p-12" data-id="element-166">
            <div className="flex justify-center items-baseline mb-8 text-center" data-id="element-167">
              <span className="text-5xl font-bold text-brand-charcoal-dark" data-id="element-168">
                $6.36
              </span>
              <span className="text-gray-500 ml-2 line-through text-xl" data-id="element-169">
                $29.99
              </span>
            </div>

            <p className="text-lg text-brand-charcoal/80 mb-8 max-w-lg mx-auto text-center" data-id="element-170">
              Por el precio de un par de cafés, obtendrás el sistema exacto para
              drenar la ansiedad y recuperar tu enfoque.
            </p>

            <div className="mb-8 space-y-3" data-id="element-171">
              <p className="text-xs font-bold text-brand-charcoal/40 tracking-wider uppercase text-center mb-2" data-id="element-172">
                Lo que incluye tu acceso
              </p>
              {includedItems.map((item, i) => {
              const isExpanded = expandedIndex === i;
              const hasDetails = item.isBonus && item.description;
              return <div key={i} className="rounded-lg border border-brand-gold/10 overflow-hidden" data-id="element-173">
                    <div className="flex items-center gap-4 bg-brand-cream/60 p-3" data-id="element-174">
                      {item.image ? <img src={item.image} alt={item.label} className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg shadow-sm flex-shrink-0" data-id="element-175" /> : <div className="w-14 h-14 sm:w-16 sm:h-16 bg-brand-charcoal-dark rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-176">
                          <span className="text-brand-gold font-serif text-xs font-bold leading-tight text-center" data-id="element-177">
                            Cortisol
                            <br data-id="element-178" />
                            Off
                          </span>
                        </div>}
                      <div className="flex-1 min-w-0" data-id="element-179">
                        <div className="flex items-center gap-2" data-id="element-180">
                          {item.isBonus ? <Gift className="h-4 w-4 text-brand-gold-dark flex-shrink-0" data-id="element-181" /> : <Check className="h-4 w-4 text-brand-sage flex-shrink-0" data-id="element-182" />}
                          <span className="font-medium text-brand-charcoal-dark text-sm sm:text-base truncate" data-id="element-183">
                            {item.label}
                          </span>
                        </div>
                        {item.isBonus && <span className="text-xs text-brand-gold-dark font-medium ml-6" data-id="element-184">
                            GRATIS
                          </span>}
                      </div>
                      {hasDetails && <button onClick={() => setExpandedIndex(isExpanded ? null : i)} className="flex items-center gap-1 text-xs font-medium text-brand-gold-dark hover:text-brand-gold transition-colors flex-shrink-0 cursor-pointer" aria-label={isExpanded ? 'Cerrar detalles' : 'Leer más'} data-id="element-185">
                          <span className="hidden sm:inline" data-id="element-186">
                            {isExpanded ? 'Cerrar' : 'Leer más'}
                          </span>
                          {isExpanded ? <ChevronUp className="h-4 w-4" data-id="element-187" /> : <ChevronDown className="h-4 w-4" data-id="element-188" />}
                        </button>}
                    </div>

                    <AnimatePresence data-id="element-189">
                      {isExpanded && hasDetails && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: [0.16, 1, 0.3, 1]
                  }} className="overflow-hidden" data-id="element-190">
                          <div className="px-4 pb-5 pt-3 bg-brand-cream/30 border-t border-brand-gold/10" data-id="element-191">
                            <p className="text-sm text-brand-charcoal/70 leading-relaxed mb-4" data-id="element-192">
                              {item.description}
                            </p>

                            {item.bullets && <ul className="space-y-2 mb-4" data-id="element-193">
                                {item.bullets.map((bullet, bi) => <li key={bi} className="flex items-start gap-2" data-id="element-194">
                                    <Check className="h-4 w-4 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-195" />
                                    <span className="text-sm text-brand-charcoal/70" data-id="element-196">
                                      {bullet}
                                    </span>
                                  </li>)}
                              </ul>}

                            {item.quote && <div className="bg-white p-3 rounded-lg border-l-4 border-brand-gold italic text-sm text-brand-charcoal/60" data-id="element-197">
                                "{item.quote}"
                              </div>}

                            {item.closing && <p className="text-sm text-brand-charcoal-dark font-medium mt-3" data-id="element-198">
                                {item.closing}
                              </p>}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>

            <div className="text-center" data-id="element-199">
              <Button size="lg" className="w-full text-base sm:text-lg mb-3 shadow-xl shadow-brand-gold/20" onClick={() => {
              trackAddToCart();
              window.open('https://pay.hotmart.com/K104693298R?off=hiid2a6g', '_blank');
            }} data-id="element-200">
                Obtener Acceso Inmediato
              </Button>

              <Button size="md" variant="outline" className="w-full text-base mb-6" onClick={() => {
              trackAddToCart();
              window.open('https://thehabitbalance.com/cart/51805726671131:1', '_blank');
            }} data-id="element-201">
                Checkout seguro
              </Button>

              <div className="flex flex-col md:flex-row justify-center items-center gap-6 text-sm text-gray-500 mb-8" data-id="element-202">
                <div className="flex items-center" data-id="element-203">
                  <Shield className="h-4 w-4 mr-2 text-brand-gold-dark" data-id="element-204" />
                  Pago 100% Seguro
                </div>
                <div className="flex items-center" data-id="element-205">
                  <Star className="h-4 w-4 mr-2 text-brand-gold-dark" data-id="element-206" />
                  Acceso de por vida
                </div>
              </div>

              <div className="bg-brand-cream rounded-lg p-6 border border-brand-gold/10" data-id="element-207">
                <h4 className="font-bold text-brand-charcoal-dark mb-2 flex items-center justify-center gap-2" data-id="element-208">
                  <Shield className="h-5 w-5 text-brand-gold-dark" data-id="element-209" />
                  Garantía de Hierro de 30 Días
                </h4>
                <p className="text-sm text-brand-charcoal/70" data-id="element-210">
                  Si no sientes una diferencia notable en tu claridad mental y
                  niveles de estrés en los primeros 7 días, te devolvemos el
                  100% de tu dinero. Sin preguntas.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>;
}